import { getCaseNode, getNodeParams } from '@/api/nodes';
import { findConversionByUnit } from '@/utils/unitUtils';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { SIZED_BY_NAMEPLATE } from '@/components/nodes/capacityMode';

const initialValues = null;
const useAnchorStore = create()(immer(() => initialValues));

const { setState: set } = useAnchorStore;

// actions
export const anchorActions = {
  loadInfo: async (caseId, nodeId) => {
    const [
      { data },
      {
        data: { params },
      },
    ] = await Promise.all([getCaseNode(caseId, nodeId), getNodeParams(caseId, nodeId)]);

    const capacityFactor = params?.capacity_factor?.primary?.value ?? null;
    const sizingBasis = params?.sizing_basis?.primary?.value ?? null;
    const { inputs: input, outputs: output } = data.conversions ?? {};

    set({ input, output, capacityFactor, sizingBasis });
  },
  clear: () => set(initialValues, true),
};

// selectors

export const useConversionsByTypeName = (ioType, ioName) =>
  useAnchorStore(store => {
    const { base_unit, conversions = [] } = store?.[ioType]?.[ioName] ?? {};

    return [{ unit: base_unit, factor: 1 }, ...conversions].reduce((acc, conversion) => {
      acc.push(conversion);
      return acc;
    }, []);
  });

export const useConversionsByMeasures = (ioType, ioName) =>
  useAnchorStore(store => {
    const { base_unit, conversions = [] } = store?.[ioType]?.[ioName] ?? {};

    return [{ unit: base_unit, factor: 1 }, ...conversions].reduce((acc, conversion) => {
      const measure = conversion?.unit?.measure;

      if (measure) {
        acc[measure] = acc[measure] ?? [];
        acc[measure].push(conversion);
      }

      return acc;
    }, {});
  });

export const useFindConversion = (ioType, ioName, unit) => {
  return useAnchorStore(store => {
    const { base_unit: baseUnit, conversions = [] } = store?.[ioType]?.[ioName] ?? {};

    // if unit is not present on the case return base unit
    if (!unit) {
      return { unit: baseUnit, factor: 1 };
    }

    const conversion = findConversionByUnit(conversions, unit);

    return conversion ?? { unit: baseUnit, factor: 1 };
  });
};

export const useCapacityFactor = () => useAnchorStore(store => store?.capacityFactor);

export const useIsSizedByNameplate = () => useAnchorStore(store => store?.sizingBasis === SIZED_BY_NAMEPLATE);

export default useAnchorStore;
